import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, Form, message, Row} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {PageType} from '@/services/basic/pagetype';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import DataStore from '@/components/GridWidget/DataStore';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {initApplyPageContext, ViewEntityProps} from '../../components/data';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import PaymentMktDetailsForApplyPanel from '../components/detail';
import {FormSpMkt} from "@/services/mkt/sp";
import FormSaMktInfosApp from "@/pages/mkt/sa/components/FormSaMktInfos";
import {WdiSelect, WdiSelectFormat} from "@/components/WdiControls/WdiSelect";
import {VatType} from "@/services/mdm/vattype";
import {Invoices} from "@/services/sales/financial/invoices";
import FormInvoiceForApplyPanel from "@/pages/sales/sp/components/FormInvoice";
import {BankAccount} from "@/services/mdm/bankaccount";
import {WarningInfoAppProps} from "@/components/WdiBus/PanelForWarning";
import SPMktWarningInfoApp from "@/pages/mkt/sp/components/WarningInformation";
import {OaContractSelectApp} from "@/pages/sales/sp/components/OaContractCmpt";
import {OaForm} from "@/services/mdm/oaform";
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {DataOptions} from "@/services/basic/metadata";
import {SpPreSelectApp} from "@/pages/sales/sp/components/SpPreSelectApp";


const getPageResult = (values: any, viewEntity: ViewEntityProps, pageType: API.PageType, statusId: number, dataSource: any[], invoiceDataSource: any[],) => {
  const result: WdiModalAppResult = {code: ResponseStatus.UNKNOWN};

  const {
    vatTypeId,
    invoiceStatus,
    branchCompanyId,
    attachment,
    bankNo,
    bankAccountId,
    oaFormNo,
    reimburseType,
    isReversePrepay,
    reversePrepayDetail,
    payToTypeId,
    payToUserId,
    payTypeId,
    ...entity
  } = values;
  if (FileUploadUtility.isUploading(attachment)) {
    result.message = '附件正在上传中!';
    return result;
  }
  if (FileUploadUtility.isError(attachment)) {
    result.message = '附件存在错误文件!';
    return result;
  }
  const req: { form: any, formSpMkt: any, formSpMktDetails: any[], invoices: any[], sapDetails: any[] } = {
    form: {
      statusId: statusId,
      pageTypeId: pageType.pageTypeId,
      pageGroupId: pageType.pageGroupId,
      formTypeId: viewEntity.formTypeId,
      userId: viewEntity.userId,
      positionId: viewEntity.positionId,
      deptId: viewEntity.deptId,
    },
    formSpMkt: {},
    formSpMktDetails: [],
    invoices: [],
    sapDetails: []
  };

  if (viewEntity.formSpMkt) {
    if (viewEntity.relationType === Enum.FormRelationType.REJECTED) {
      // 退回待修改
      req.form.relatedFormId = viewEntity.relatedFormId;
      req.form.relationType = viewEntity.relationType;
    } else {
      // 从草稿打开
      req.form.formId = viewEntity.formId;
    }
  }

  entity.modifiedReason = '新建';
  entity.formSaMktId = viewEntity.formSaMktId;
  entity.invoiceStatus = WdiSelectFormat.getvalue(invoiceStatus);
  entity.branchCompanyId = WdiSelectFormat.getvalue(branchCompanyId);
  entity.bankNo = WdiSelectFormat.getvalue(bankNo);
  entity.bankAccountId = WdiSelectFormat.getvalue(bankAccountId);
  entity.payToTypeId = viewEntity.payToTypeId;
  entity.payToUserId = WdiSelectFormat.getvalue(payToUserId);
  entity.payTypeId = WdiSelectFormat.getvalue(payTypeId);

  if (oaFormNo) {
    entity.oaFormNo = oaFormNo.oaFormNo;
    entity.oaFormName = oaFormNo.oaFormName;
    entity.url = oaFormNo.url;
  }
  entity.isReversePrepay = WdiSelectFormat.getvalue(isReversePrepay);

  if (reversePrepayDetail) {
    entity.reversePrepayDetail = values.reversePrepayDetail.map((item: any) => wdiCore.String.CodeOrName(item?.requisitionNumber, item?.transactionNum)).filter((item: any) => item).join()
  }
  if (WdiSelectFormat.getvalue(reimburseType) == true) {
    entity.reimburseType = Enum.ReimburseType.PRE_PAY;
  } else {
    entity.reimburseType = Enum.ReimburseType.DEFAULT;
  }

  if (entity.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE && invoiceDataSource.length < 1) {
    result.message = '请填写发票信息！'
    return result;
  }

  const info = FileUploadUtility.serialize(attachment);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;
  req.formSpMkt = entity;
  req.formSpMktDetails = dataSource;
  req.invoices = invoiceDataSource;
  for (let i = 0; i < req.formSpMktDetails.length; i++) {
    const detailRow = req.formSpMktDetails[i];
    detailRow.formSpMktDetailId = detailRow.formSpMktDetailId || 0;
    detailRow.formSpMktId = detailRow.formSpMktId || 0;
    detailRow.vatTypeId = WdiSelectFormat.getvalue(vatTypeId);
  }
  for (let i = 0; i < req.invoices.length; i++) {
    const detailRow = req.invoices[i];
    detailRow.keyInDate = null;
  }

  result.data = req;
  result.code = ResponseStatus.SUCCESS;
  return result;
};

const PaymentMktApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [vatType, setVatType] = useState<API.VatType>();
  const [invoiceDataSource, setInvoiceDataSource] = useState<any[]>([]);
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [payTypeId, setPayTypeId] = useState(0);
  const [editorRef, setEditorRef] = useState<any>();
  const [isReversePrepay, setIsReversePrepay] = useState(false);

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const onInvoiceChange = (data: any) => {
    setInvoiceDataSource(data);
  }

  const onVatTypeChange = (vatTypeId?: number) => {
    setVatType(undefined);
    if (vatTypeId) {
      VatType.get(vatTypeId).then(responseResult => {
        setVatType(responseResult.data);
        if (dataSource && dataSource.length > 0) {
          const newDataSource = [...dataSource];
          newDataSource.forEach(item => {
            item.vatTypeId = vatTypeId;
            item.vatTypeValue = responseResult.data.vatTypeValue;
            if (!wdiCore.Object.IsNullOrUndefined(item.amountInVat) && !wdiCore.Object.IsNullOrUndefined(item.vatTypeValue)) {
              item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + item.vatTypeValue), 2);
              item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
            }
          })
          setDataSource(newDataSource);
        }
      })
    }
  }

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSpMkt) {
          formRef.current?.setFieldsValue({
            saCloseOption: viewEntity.formSpMkt.saCloseOption,
            remark: viewEntity.formSpMkt.remark,
            attachment: FileUploadUtility.parse(viewEntity.formSpMkt.fileName, viewEntity.formSpMkt.filePath),
            isReversePrepay: viewEntity.formSpMkt.isReversePrepay,
            payTypeId: viewEntity.formSpMkt.payTypeId,
            payToTypeId: viewEntity.formSpMkt.payToTypeId,
            payToUserId: viewEntity.formSpMkt.payToUserId,
            bankAccountName: viewEntity?.formSpMkt?.bankAccountName,
          });

          if (viewEntity.formSpMkt?.reversePrepayDetail) {
            const strArray = viewEntity.formSpMkt?.reversePrepayDetail.split(',');
            const reversePrepayDetail = strArray.map(str => {
              const [requisitionNumber, transactionNum] = str.split(' - ');
              return {
                requisitionNumber,
                transactionNum,
              };
            });
            formRef.current?.setFieldsValue({
              reversePrepayDetail: reversePrepayDetail
            });
          }
          Invoices.getForPayment(viewEntity.formSpMkt.formSpMktId || 0).then(responseResult => {
            let invoices = DataStore.init(responseResult.data);
            if (viewEntity.form?.relationType) {
              invoices = invoices.map(invoice => (Object.assign({}, invoice, {formInvoiceId: null})))
            }
            setInvoiceDataSource(invoices);
          })

          if (viewEntity?.formSpMkt?.bankAccountId) {
            BankAccount.get(viewEntity?.formSpMkt?.bankAccountId).then(res => {
              formRef.current?.setFieldsValue({
                bankAccountId: res?.data?.bankAccountId,

              })
            })
          }


          if (viewEntity.formSpMkt?.oaFormNo) {
            OaForm.get(viewEntity.formSpMkt?.oaFormNo).then(res => {
              formRef.current?.setFieldsValue({
                oaFormNo: res.data,
              });
            })
          }
          if (viewEntity.formSpMkt?.isReversePrepay) {
            setIsReversePrepay(true)
          }
        }
        if (viewEntity.formSpMkt && viewEntity.formSpMkt.payTypeId) {
          setPayTypeId(viewEntity.formSpMkt.payTypeId);
        } else {
          formRef.current?.setFieldsValue({
            payTypeId: 1
          })
          setPayTypeId(1)
        }

        if (viewEntity.payToUser) {
          formRef.current?.setFieldsValue({
            payToUser: wdiCore.String.GenerateLabelDescription(viewEntity.payToUser.staffNoOrName, viewEntity.payToUser.title),
            payToUserId: viewEntity.payToUser.staffId
          });
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodId: viewEntity.paymentMethod.value,
            paymentMethodName: viewEntity.paymentMethod.name,
          });
        }
        if (viewEntity.supplier) {
          formRef.current?.setFieldsValue({
            supplierId: viewEntity.supplier.supplierId,
            supplierName: viewEntity.supplier.codeOrName,
          })
          if (viewEntity.formSpMkt?.invoiceStatus != null || viewEntity.formSpMkt?.invoiceStatus != undefined) {
            formRef.current?.setFieldsValue({
              invoiceStatus: viewEntity.formSpMkt.invoiceStatus
            })
            onInvoiceStatus(viewEntity.formSpMkt?.invoiceStatus);
          } else {
            formRef.current?.setFieldsValue({
              invoiceStatus: Enum.InvoiceStatus.WITH_INVOICE
            })
          }

        }
        if (viewEntity.company) {
          formRef.current?.setFieldsValue({
            companyCode: viewEntity.company.companyCode,
            companyName: viewEntity.company.codeOrName,
          })
        }
        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }

        if (viewEntity.formSpMktDetails && viewEntity.formSpMktDetails.length > 0) {
          await VatType.get(Number(viewEntity.formSpMktDetails[0].vatTypeId)).then(res => {
            if (res.data) {
              setVatType(res?.data);
              formRef.current?.setFieldsValue({
                vatTypeId: {value: res?.data?.vatTypeId, label: res?.data?.vatTypeName}
              });
            }
          });
          setDataSource(DataStore.init(viewEntity.formSpMktDetails));
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  const onInvoiceStatus = (invoiceStatus?: number) => {
    if (invoiceStatus != Enum.InvoiceStatus.WITH_INVOICE) {
      setInvoiceDataSource([]);
    }
  }

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = '活动核销申请';
      if (entity.formSpMkt) {
        title += '【编辑】';
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPMktWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        initialValues={{invoiceStatus: Enum.InvoiceStatus.WITH_INVOICE}}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl='/mkt/sp/mypayment'
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId = type === ApplyFooterType.Draft ? Enum.FormStatus.DRAFT : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(values, entity, pageType, statusId, dataSource, invoiceDataSource);
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSpMkt.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSpMkt.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '提示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <FormSaMktInfosApp formRef={formRef} defaultCollapsed={true} formSaMktId={entity?.formSaMktId}/>
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title='活动核销单信息'/>}>
          <Row gutter={[16, 24]}>
            <Form.Item name='invoiceStatus' label='发票状态' hidden>
              <WdiInput readOnly/>
            </Form.Item>
            <Col className='gutter-row' span={4}>
              <Form.Item name='vatTypeId' label='税率类型' rules={[{required: true}]}>
                <WdiSelect {...{
                  placeholder: "请选择税率类型",
                  showSearch: true,
                  dataLoading: async (params) => {
                    const response = await VatType.search(Object.assign({isActive: true}, params));
                    return response.data.map((item) => {
                      return {value: item.vatTypeId, label: item.vatTypeName};
                    });
                  },
                  onChange: (params) => {
                    onVatTypeChange(params);
                  },
                }}/>
              </Form.Item>
            </Col>
            <Col className='gutter-row' span={4}>
              <Form.Item name="isReversePrepay" label="是否冲销预付" valuePropName="checked">
                <Checkbox onChange={(e) => {
                  setIsReversePrepay(e.target.checked)
                  if (!e.target.checked) {
                    formRef.current?.setFieldsValue({
                      reversePrepayDetail: null
                    });
                  }
                }}/>
              </Form.Item>
            </Col>
            {isReversePrepay && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="reversePrepayDetail" label="冲销的预付单"
                           rules={[{required: isReversePrepay, message: "冲销的预付单不能为空!"}]}>
                  <SpPreSelectApp
                    filterParams={{
                      paymentMethodId: Enum.PaymentMethod.THIRD_PART,
                      companyCode: entity?.companyCode,
                      payToUserId: entity?.payToUserId,
                      supplierId: entity?.supplierId,
                      formSpDetail: entity?.formSpMktDetails?.[0] ?? {}
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4}>
              <Form.Item name="payTypeId" label="支付类型" rules={[{required: true, message: "支付类型不能为空!"}]}>
                <WdiSelect
                  {...{
                    placeholder: '请选择支付类型',
                    dataLoading: async (params) => {
                      const response = await DataOptions.search(Object.assign({
                        typeGroup: 106,
                        isActive: true
                      }, params));
                      return response.data.map((item) => {
                        return {value: item.value, label: item.name};
                      });
                    },
                    onChange: (value) => {
                      setPayTypeId(value);
                      formRef.current?.setFieldsValue({bankAccountId: null, bankAccountName: null});
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className='gutter-row' span={4}>
              <Form.Item name='bankAccountId' label='银行账号' rules={[{required: payTypeId == Enum.PayType.CASH}]}>
                <WdiSelect
                  {...{
                    placeholder: '请选择银行账号',
                    showSearch: true,
                    remote: true,
                    dataLoading: async (params) => {
                      const param: API.BankAccountFilter = {isActive: true};
                      if (entity?.payToUserId) {
                        param.userId = entity?.payToUserId;
                      } else {
                        param.supplierId = entity?.supplierId;
                        param.companyCode = entity?.companyCode;
                      }
                      const response = await BankAccount.ajaxSearch(Object.assign(param, params,));
                      return response.data.map((item) => {
                        return {value: item.bankAccountId, label: item.bankAccountNo};
                      });
                    },
                    onChange: (value) => {
                      formRef.current?.setFieldsValue({bankAccountName: null});
                      if (value) {
                        BankAccount.get(value).then((res) => {
                          formRef.current?.setFieldsValue({
                            bankAccountName: res?.data?.bankAccountName,
                          });
                        });
                      }
                    },
                    reloadKey: `${entity?.supplierId}${entity?.companyCode}`,
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="bankAccountName" label="户名" rules={[{required: payTypeId == Enum.PayType.CASH}]}>
                <WdiInput/>
              </Form.Item>
            </Col>

          </Row>
          <Row gutter={[16, 24]}>
            <Col className='gutter-row' span={4}>
              <Form.Item
                name="oaFormNo" label="OA合同号"
                //rules={[{required: true, message: 'OA合同号不能为空'}]}
              >
                <OaContractSelectApp/>
              </Form.Item>
            </Col>
            <Col className='gutter-row' span={4}>
              <Form.Item name='paymentMethodName' label='支付方式'>
                <WdiInput readOnly/>
              </Form.Item>
              <Form.Item name='paymentMethodId' label='paymentMethodId' hidden>
                <WdiInput readOnly/>
              </Form.Item>
            </Col>

            <Col className='gutter-row' span={4}>
              <Form.Item name='companyName' label='公司'>
                <WdiInput readOnly/>
              </Form.Item>
              <Form.Item name='companyCode' label='companyCode' hidden>
                <WdiInput readOnly/>
              </Form.Item>
            </Col>
            {entity?.supplier && (<Col className='gutter-row' span={4}>
                <Form.Item name='supplierName' label='供应商'>
                  <WdiInput readOnly/>
                </Form.Item>
                <Form.Item name='supplierId' label='supplierId' hidden>
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}
            {entity?.payToUser && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="payToUser" label="员工">
                  <WdiInput readOnly/>
                </Form.Item>
                <Form.Item name='payToUserId' label='payToUserId' hidden>
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}

          </Row>
          <Row gutter={[16, 24]}>
            <Col className='gutter-row' span={16}>
              <Form.Item name='remark' label='备注'>
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className='gutter-row' span={8}>
              <Form.Item
                name='attachment'
                label='附件'
                valuePropName='fileList'
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo='请选择文件并上传(单个文件不能超500M)'/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <PaymentMktDetailsForApplyPanel
          data={{
            pageTypeId: pageType?.pageTypeId,
            formSaMktId: entity?.formSaMktId,
            paymentMethodId: entity?.paymentMethodId,
            vatType: vatType
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
        <FormInvoiceForApplyPanel
          data={{
            details: dataSource,
            vatType: vatType,
            paymentMethodId: entity?.paymentMethodId,
            supplierId: entity?.supplierId,
            companyCode: entity?.companyCode,
            payToUserId: entity?.payToUserId,
          }}
          onChange={onInvoiceChange}
          dataSource={invoiceDataSource}
        />
      </ProForm>
    </>
  );
};
export default PaymentMktApplyApp;
